import { uniq } from 'lodash-es';

import type { TableSelectionType } from '@intelligence/components/TableComponents/components/TableCheckbox';
import type { IRobloxGameRankRow } from '../types';

export interface IRobloxRankChartSeriesItem {
  id: string;
  name: string;
  cover: string;
  color?: string;
  history: Array<{
    metric: string;
    source: string;
    time: string;
    value: number;
  }>;
}

export const getRobloxRankChartXAxisDates = (
  list: IRobloxGameRankRow[],
  selectedSource: string,
): string[] => {
  const dates = list
    .filter((item) => item?.source === selectedSource && item?.date)
    .map((item) => String(item.date));

  return uniq(dates).sort((a, b) => momentCompare(a, b));
};

export const transformRobloxRankChartLineSeries = (
  list: IRobloxGameRankRow[],
  tableSelection: TableSelectionType[],
  selectedSource: string,
): IRobloxRankChartSeriesItem[] => {
  const selectedIds = tableSelection
    .map((item) => String(item?.gameId || (item as IRobloxGameRankRow)?.game_id || ''))
    .filter(Boolean);

  if (!selectedIds.length) {
    return [];
  }

  const seriesMap = new Map<string, IRobloxRankChartSeriesItem>();

  list.forEach((row) => {
    if (row?.source !== selectedSource) {
      return;
    }

    const gameId = String(row?.game_id || '');
    if (!gameId || !selectedIds.includes(gameId)) {
      return;
    }

    if (!seriesMap.has(gameId)) {
      const selectionItem = tableSelection.find(
        (item) => String(item?.gameId || (item as IRobloxGameRankRow)?.game_id) === gameId,
      );

      seriesMap.set(gameId, {
        id: gameId,
        name: row?.entity_name || gameId,
        cover: row?.cover || '',
        color: selectionItem?.color,
        history: [],
      });
    }

    if (!row?.date) {
      return;
    }

    const rankValue = Number(row.rank);
    seriesMap.get(gameId)?.history.push({
      metric: 'rank',
      source: '',
      time: String(row.date),
      value: Number.isFinite(rankValue) ? rankValue : Number.NaN,
    });
  });

  return selectedIds
    .map((gameId) => {
      const seriesItem = seriesMap.get(gameId);
      if (!seriesItem) {
        const selectionItem = tableSelection.find(
          (item) => String(item?.gameId || (item as IRobloxGameRankRow)?.game_id) === gameId,
        );

        return {
          id: gameId,
          name: String(selectionItem?.gametitle || selectionItem?.entity_name || gameId),
          cover: String(selectionItem?.img || selectionItem?.cover || ''),
          color: selectionItem?.color,
          history: [],
        };
      }

      seriesItem.history.sort((a, b) => momentCompare(a.time, b.time));
      return seriesItem;
    })
    .filter(Boolean);
};

const momentCompare = (left: string, right: string): number => {
  if (left === right) {
    return 0;
  }

  return left > right ? 1 : -1;
};
